import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Award, Loader } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';

const AffiliateAutoLogin = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthenticatedUser } = useContext(AuthContext);
  const [status, setStatus] = useState('loading'); // loading, error, success
  const [message, setMessage] = useState('Authenticating...');

  useEffect(() => {
    const token = searchParams.get('token');
    
    console.log('[Affiliate Auth] Component mounted');
    console.log('[Affiliate Auth] Token from URL:', token ? token.substring(0, 20) + '...' : 'NO TOKEN');
    
    if (!token) {
      console.error('[Affiliate Auth] No token in URL');
      setStatus('error');
      setMessage('No access token provided. Please use the link from your approval email.');
      return;
    }

    authenticateAffiliate(token);
  }, [searchParams]);

  const authenticateAffiliate = async (token) => {
    try {
      console.log('[Affiliate Auth] Starting authentication with token:', token.substring(0, 20) + '...');
      
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const fullUrl = `${apiUrl}/affiliates/auth?token=${token}`;
      
      console.log('[Affiliate Auth] API URL:', fullUrl);
      
      const response = await fetch(fullUrl);
      
      console.log('[Affiliate Auth] Response status:', response.status);
      console.log('[Affiliate Auth] Response ok:', response.ok);

      const data = await response.json();
      console.log('[Affiliate Auth] Response data:', data);

      if (response.ok && data.success) {
        console.log('[Affiliate Auth] Authentication successful!');
        
        // Backend wraps response in { success: true, data: {...} }
        const { user, token } = data.data || data;
        console.log('[Affiliate Auth] User data:', user);
        console.log('[Affiliate Auth] Token:', token ? 'EXISTS' : 'MISSING');
        
        // Set authenticated user with JWT token - MUST pass as object
        try {
          await setAuthenticatedUser({ token, user });
          console.log('[Affiliate Auth] User set successfully');
        } catch (authError) {
          console.error('[Affiliate Auth] Error setting user:', authError);
          throw authError;
        }
        
        setStatus('success');
        setMessage('Authentication successful! Redirecting to dashboard...');
        
        // Redirect to dashboard after a brief delay
        setTimeout(() => {
          navigate('/affiliate/dashboard', { replace: true });
        }, 1500);
      } else {
        console.error('[Affiliate Auth] Authentication failed:', data.error);
        setStatus('error');
        setMessage(data.error || 'Authentication failed. Please contact support.');
      }
    } catch (error) {
      console.error('[Affiliate Auth] Caught error:', error);
      console.error('[Affiliate Auth] Error name:', error?.name);
      console.error('[Affiliate Auth] Error message:', error?.message);
      console.error('[Affiliate Auth] Error stack:', error?.stack);
      
      setStatus('error');
      setMessage('An error occurred during authentication. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white shadow-2xl rounded-2xl p-8 text-center">
          <div className={`mx-auto h-20 w-20 rounded-full flex items-center justify-center mb-6 ${
            status === 'loading' ? 'bg-purple-100' :
            status === 'success' ? 'bg-green-100' :
            'bg-red-100'
          }`}>
            {status === 'loading' && (
              <Loader className="h-12 w-12 text-purple-600 animate-spin" />
            )}
            {status === 'success' && (
              <Award className="h-12 w-12 text-green-600" />
            )}
            {status === 'error' && (
              <Award className="h-12 w-12 text-red-600" />
            )}
          </div>

          <h2 className={`text-2xl font-bold mb-4 ${
            status === 'loading' ? 'text-gray-900' :
            status === 'success' ? 'text-green-600' :
            'text-red-600'
          }`}>
            {status === 'loading' && 'Verifying Access'}
            {status === 'success' && 'Welcome Back!'}
            {status === 'error' && 'Authentication Failed'}
          </h2>

          <p className="text-gray-600 mb-6">
            {message}
          </p>

          {status === 'loading' && (
            <div className="flex justify-center space-x-2">
              <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Try Again
              </button>
              <a
                href="/affiliate/signup"
                className="block text-sm text-purple-600 hover:text-purple-700"
              >
                Don't have an account? Apply now
              </a>
            </div>
          )}
        </div>

        {status === 'error' && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
            <p className="font-semibold mb-2">Need help?</p>
            <p>If you just received approval, make sure you're using the exact link from your email. Contact support if the problem persists.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateAutoLogin;
